import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { X } from "lucide-react";

export function CalendarDayCell({ dateStr, bookings, isToday, isSelected, crewColorMap, crewNameMap, onClick }) {
  const dayNum = parseInt(dateStr.split("-")[2]);
  const uniqueCrew = [...new Set(bookings.map(b => b.crew_id))];
  const uniqueEvents = [...new Set(bookings.map(b => b.event_name))];

  return (
    <button
      data-testid={`cal-day-${dateStr}`}
      className={`bg-card min-h-[100px] p-2 text-left transition-colors hover:bg-secondary/30 relative
        ${isSelected ? 'ring-2 ring-primary ring-inset' : ''}
        ${isToday ? 'bg-primary/5' : ''}`}
      onClick={onClick}
    >
      <span className={`text-xs font-medium ${isToday ? 'bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center' : 'text-muted-foreground'}`}>
        {dayNum}
      </span>
      {uniqueCrew.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {uniqueCrew.map(cid => (
            <span key={cid} className={`w-2.5 h-2.5 rounded-full ${crewColorMap[cid]} opacity-80`} title={crewNameMap[cid]} />
          ))}
        </div>
      )}
      {bookings.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {uniqueEvents.slice(0, 3).map(name => (
            <p key={name} className="text-[10px] text-muted-foreground truncate leading-tight">{name}</p>
          ))}
          {uniqueEvents.length > 3 && (
            <p className="text-[10px] text-primary font-medium">+{uniqueEvents.length - 3} more</p>
          )}
        </div>
      )}
    </button>
  );
}


export function CalendarDayDetail({ selectedDay, data, crewColorMap, crewNameMap, onClose }) {
  const dayDetail = useMemo(() => {
    if (!selectedDay || !data) return null;
    const bookings = data.days[selectedDay] || [];
    const bookedCrewIds = new Set(bookings.map(b => b.crew_id));
    const eventMap = {};
    bookings.forEach(b => {
      if (!eventMap[b.event_name]) eventMap[b.event_name] = [];
      eventMap[b.event_name].push(b.crew_id);
    });
    const available = (data.crew || []).filter(c => !bookedCrewIds.has(c.crew_id));
    return { eventMap, available };
  }, [selectedDay, data]);

  if (!dayDetail) return null;

  return (
    <Card className="w-full lg:w-72 shrink-0 border-border lg:self-start lg:sticky lg:top-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {new Date(selectedDay + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {Object.keys(dayDetail.eventMap).length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400">Booked</p>
            {Object.entries(dayDetail.eventMap).map(([eventName, crewIds]) => (
              <div key={eventName} className="border-l-2 border-primary/40 pl-2">
                <p className="text-xs font-medium text-primary">{eventName}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {crewIds.map(cid => (
                    <div key={cid} className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${crewColorMap[cid]}`} />
                      <span className="text-xs text-muted-foreground">{crewNameMap[cid]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {dayDetail.available.length > 0 && (
          <div className={Object.keys(dayDetail.eventMap).length > 0 ? "mt-4 pt-3 border-t border-border" : ""}>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2">Available</p>
            <div className="space-y-1">
              {dayDetail.available.map(c => (
                <div key={c.crew_id} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${crewColorMap[c.crew_id]}`} />
                  <span className="text-xs">{c.name}</span>
                  {(c.roles || []).length > 0 && (
                    <span className="text-[10px] text-muted-foreground ml-auto">{(c.roles || []).join(", ")}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {dayDetail.available.length === 0 && Object.keys(dayDetail.eventMap).length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No bookings or crew</p>
        )}
      </CardContent>
    </Card>
  );
}
